import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSeoPagesEditor } from '@/contexts/SeoPagesEditorContext';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useAuth } from '@/hooks/useAuth';
import { useGrowthSquadMembers } from '@/hooks/useGrowthSquadMembers';
import { useCreateGrowthTask } from '@/hooks/useCreateGrowthTask';
import { supabase } from '@/integrations/supabase/client';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { useSidebar } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  RefreshCw,
  FileText,
  Eye,
  MousePointer,
  Target,
  TrendingUp,
  ListPlus,
  X,
  CheckCircle,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { invokeWithTimeout } from '@/utils/edgeFunctionWithTimeout';

interface PageAnalysis {
  id: string;
  page_url: string;
  total_clicks: number;
  total_impressions: number;
  avg_ctr: number;
  avg_position: number;
  performance_score: number;
  content_quality_score: number | null;
  queries: Array<{
    query: string;
    clicks: number;
    impressions: number;
    position: number;
    ctr: number;
  }>;
  created_at: string;
  task_id?: string | null;
}

type SortField = 'page_url' | 'total_clicks' | 'total_impressions' | 'avg_ctr' | 'avg_position' | 'performance_score';
type SortDirection = 'asc' | 'desc';

export function GlobalSeoPagesEditor() {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const { isOpen, closeEditor } = useSeoPagesEditor();
  const { currentBrandId, currentBrand } = useBrandManager();
  const { toast } = useToast();
  const { state: sidebarState, isMobile } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { members } = useGrowthSquadMembers();
  const { createTask, isCreating } = useCreateGrowthTask();

  // Close drawer when route changes
  useEffect(() => {
    if (isOpen) {
      closeEditor();
    }
  }, [location.pathname]);

  // Calculate drawer width and top offset based on sidebar state
  // Use fixed values since CSS variables are scoped to SidebarProvider
  const SIDEBAR_WIDTH = '16rem'; // 256px
  const SIDEBAR_WIDTH_ICON = '3rem'; // 48px
  const MOBILE_HEADER_HEIGHT = '57px'; // Height of MobileHeader
  
  const drawerWidth = isMobile 
    ? '100%' 
    : sidebarState === 'expanded' 
      ? `calc(100% - ${SIDEBAR_WIDTH})` 
      : `calc(100% - ${SIDEBAR_WIDTH_ICON})`;
  
  const drawerTop = isMobile ? MOBILE_HEADER_HEIGHT : '0';

  const [pages, setPages] = useState<PageAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [sortField, setSortField] = useState<SortField>('performance_score');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set());
  const [addingTask, setAddingTask] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Scroll to top when drawer opens
  useEffect(() => {
    if (isOpen && scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (currentBrandId && isOpen) {
      fetchPageAnalyses();
    }
  }, [currentBrandId, isOpen]);

  const fetchPageAnalyses = async () => {
    if (!currentBrandId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('seo_gsc_page_analysis')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('performance_score', { ascending: false });

      if (error) throw error;

      const parsedData = (data || []).map((page: any) => ({
        ...page,
        queries: Array.isArray(page.queries) ? page.queries : [],
      }));

      setPages(parsedData);
    } catch (error) {
      console.error('Error fetching page analyses:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch page analyses',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzePages = async () => {
    if (!currentBrandId) return;

    try {
      setAnalyzing(true);
      toast({
        title: 'Analyzing Pages',
        description: 'Processing page performance data...',
      });

      const { data, error } = await invokeWithTimeout(supabase, 'analyze-pages', {
        body: { brand_id: currentBrandId },
        timeout: 120000,
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Analyzed ${data?.count || 0} pages`,
      });

      fetchPageAnalyses();
    } catch (error: any) {
      console.error('Error analyzing pages:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to analyze pages',
        variant: 'destructive',
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const sortedPages = useMemo(() => {
    const sorted = [...pages].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (sortField) {
        case 'page_url':
          return sortDirection === 'asc'
            ? a.page_url.localeCompare(b.page_url)
            : b.page_url.localeCompare(a.page_url);
        case 'total_clicks':
          aVal = a.total_clicks;
          bVal = b.total_clicks;
          break;
        case 'total_impressions':
          aVal = a.total_impressions;
          bVal = b.total_impressions;
          break;
        case 'avg_ctr':
          aVal = a.avg_ctr;
          bVal = b.avg_ctr;
          break;
        case 'avg_position':
          aVal = a.avg_position;
          bVal = b.avg_position;
          break;
        case 'performance_score':
          aVal = a.performance_score;
          bVal = b.performance_score;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });

    return sorted;
  }, [pages, sortField, sortDirection]);

  const paginatedPages = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedPages.slice(startIndex, endIndex);
  }, [sortedPages, currentPage, itemsPerPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'avg_position' ? 'asc' : 'desc');
    }
  };

  const togglePageExpand = (pageUrl: string) => {
    const newExpanded = new Set(expandedPages);
    if (newExpanded.has(pageUrl)) {
      newExpanded.delete(pageUrl);
    } else {
      newExpanded.add(pageUrl);
    }
    setExpandedPages(newExpanded);
  };

  const handleAddToTask = async (page: PageAnalysis) => {
    if (!currentBrandId) return;

    // Find current user's squad member ID
    const currentMember = members.find(m => m.user_id === user?.id);
    if (!currentMember) {
      toast({ 
        title: 'Error', 
        description: 'You must be a squad member to create tasks', 
        variant: 'destructive' 
      });
      return;
    }

    try {
      setAddingTask(true);

      const topQueries = page.queries
        .sort((a, b) => b.impressions - a.impressions)
        .slice(0, 3)
        .map((q) => q.query)
        .join(', ');

      let reason = '';
      let recommendation = '';

      if (page.performance_score < 50) {
        reason = `Low Performance Score (${page.performance_score})`;
        recommendation = `Improve content quality, optimize for target keywords (${topQueries}), and enhance on-page SEO elements`;
      } else if (page.avg_position > 10) {
        reason = `Position Outside Top 10 (${page.avg_position.toFixed(1)})`;
        recommendation = `Create comprehensive content targeting: ${topQueries}. Analyze top-ranking competitors and fill content gaps`;
      } else if (page.avg_ctr < 0.05) {
        reason = `Low CTR (${(page.avg_ctr * 100).toFixed(2)}%)`;
        recommendation = `Optimize title tags and meta descriptions for queries: ${topQueries}`;
      } else {
        reason = `Optimize for ${page.queries.length} ranking queries`;
        recommendation = `Focus on improving rankings for: ${topQueries}`;
      }

      // Calculate metrics for description
      const expectedCtr = page.avg_ctr * 1.5;
      const potentialClickGain = Math.round(page.total_impressions * (page.avg_ctr * 0.5));
      const priority = Math.round(100 - page.performance_score);

      // Build the task description
      const description = `**Page:** ${page.page_url}

**Keyword(s):** ${topQueries || 'N/A'}

**Recommendation:** ${recommendation}

**Metrics:**
- Priority: ${priority}
- Clicks: ${page.total_clicks}
- Impressions: ${page.total_impressions}
- CTR: ${(page.avg_ctr * 100).toFixed(2)}%
- Position: ${page.avg_position.toFixed(1)}
- Expected CTR: ${(expectedCtr * 100).toFixed(2)}%
- Potential Click Gain: ${potentialClickGain}`;

      // Create foundational task
      const taskData = await createTask({
        title: `Fix ${reason}`,
        description,
        priority: page.performance_score < 50 ? 'High' : page.performance_score < 70 ? 'Medium' : 'Low',
        difficulty: 'Medium',
        assigned_to: currentMember.id,
        brand_id: currentBrandId,
        source_type: 'seo_gsc_page',
        source_data: {
          page_analysis_id: page.id,
          page_url: page.page_url,
          performance_score: page.performance_score,
          queries: page.queries
        }
      });

      // Update page analysis with task_id
      if (taskData?.id) {
        await supabase
          .from('seo_gsc_page_analysis')
          .update({ task_id: taskData.id })
          .eq('id', page.id);

        // Refresh pages to show "Tasked" state
        fetchPageAnalyses();
      }
    } catch (error) {
      console.error('Error adding task:', error);
      toast({
        title: 'Error',
        description: 'Failed to create task',
        variant: 'destructive',
      });
    } finally {
      setAddingTask(false);
    }
  };

  const handleViewTask = async (taskId: string) => {
    // Fetch task details to get the slug
    const { data: task } = await supabase
      .from('foundational_tasks')
      .select('slug')
      .eq('id', taskId)
      .single();
    
    if (task?.slug && currentBrandId) {
      // Get brand slug
      const { data: brand } = await supabase
        .from('brands')
        .select('slug')
        .eq('id', currentBrandId)
        .single();
      
      if (brand?.slug) {
        navigate(`/task/${brand.slug}/${task.slug}`);
        return;
      }
    }
    // Fallback to tasks page with query param
    navigate(`/tasks?task=${taskId}`);
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 70) return 'Excellent';
    if (score >= 50) return 'Good';
    return 'Needs Work';
  };

  const getScoreBadgeVariant = (score: number): 'default' | 'secondary' | 'destructive' => {
    if (score >= 70) return 'default';
    if (score >= 50) return 'secondary';
    return 'destructive';
  };

  const stats = useMemo(() => {
    return {
      totalPages: pages.length,
      totalClicks: pages.reduce((sum, p) => sum + p.total_clicks, 0),
      avgScore: pages.length > 0 ? pages.reduce((sum, p) => sum + p.performance_score, 0) / pages.length : 0,
      avgPosition: pages.length > 0 ? pages.reduce((sum, p) => sum + p.avg_position, 0) / pages.length : 0,
    };
  }, [pages]);

  const SortButton = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Button variant="ghost" size="sm" onClick={() => handleSort(field)} className="h-8 gap-1 font-semibold">
      {children}
      {sortField === field ? (
        sortDirection === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </Button>
  );

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && closeEditor()} direction="right" modal={false}>
      <DrawerContent 
        className="max-w-none right-0 left-auto rounded-none mt-0 fixed bg-background border-l"
        style={{ 
          width: drawerWidth, 
          top: drawerTop,
          height: isMobile ? `calc(100% - ${MOBILE_HEADER_HEIGHT})` : '100%'
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header - Mobile: stacked rows, Desktop: horizontal */}
          <div className="px-4 sm:px-6 py-4 border-b">
            {/* Mobile Layout */}
            <div className="sm:hidden space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Page Performance</h2>
                <Button variant="ghost" size="icon" onClick={closeEditor}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Analyze page performance scores
                {currentBrand && <span className="ml-1 text-primary">({currentBrand.name})</span>}
              </p>
              <div className="flex justify-end">
                <Button onClick={handleAnalyzePages} disabled={analyzing || !currentBrandId} size="sm">
                  {analyzing ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Analyze
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-xl font-semibold">Page Performance</h2>
                  <p className="text-sm text-muted-foreground">
                    Analyze page performance scores and ranking queries
                    {currentBrand && <span className="ml-2 text-primary">({currentBrand.name})</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={handleAnalyzePages} disabled={analyzing || !currentBrandId}>
                  {analyzing ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Analyze Pages
                    </>
                  )}
                </Button>
                <Button variant="ghost" size="icon" onClick={closeEditor}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <ScrollArea ref={scrollAreaRef} className="flex-1">
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {!currentBrandId ? (
                <Card className="p-6">
                  <p className="text-muted-foreground">Please select a brand to continue.</p>
                </Card>
              ) : loading ? (
                <div className="space-y-4">
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-96 w-full" />
                </div>
              ) : (
                <>
                  {/* Stats Cards - 2 per row on mobile, 4 on desktop */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                    <Card>
                      <CardHeader className="pb-1 sm:pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                        <CardDescription className="text-xs sm:text-sm">Total Pages</CardDescription>
                      </CardHeader>
                      <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground hidden sm:block" />
                          <p className="text-xl sm:text-2xl font-bold">{stats.totalPages.toLocaleString()}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-1 sm:pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                        <CardDescription className="text-xs sm:text-sm">Total Clicks</CardDescription>
                      </CardHeader>
                      <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
                        <div className="flex items-center gap-2">
                          <MousePointer className="h-4 w-4 text-muted-foreground hidden sm:block" />
                          <p className="text-xl sm:text-2xl font-bold">{stats.totalClicks.toLocaleString()}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-1 sm:pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                        <CardDescription className="text-xs sm:text-sm">Avg Performance</CardDescription>
                      </CardHeader>
                      <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-muted-foreground hidden sm:block" />
                          <p className={`text-xl sm:text-2xl font-bold ${getScoreColor(stats.avgScore)}`}>
                            {stats.avgScore.toFixed(0)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-1 sm:pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                        <CardDescription className="text-xs sm:text-sm">Avg Position</CardDescription>
                      </CardHeader>
                      <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
                        <div className="flex items-center gap-2">
                          <Eye className="h-4 w-4 text-muted-foreground hidden sm:block" />
                          <p className="text-xl sm:text-2xl font-bold">{stats.avgPosition.toFixed(1)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Pages - Cards on mobile, Table on desktop */}
                  {pages.length === 0 ? (
                    <Card>
                      <CardContent className="flex flex-col items-center justify-center py-16">
                        <FileText className="h-16 w-16 text-muted-foreground mb-4" />
                        <h3 className="text-xl font-semibold mb-2">No Page Analyses Yet</h3>
                        <p className="text-muted-foreground mb-4 text-center">
                          Click "Analyze Pages" to process your keyword-page matching data
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      {/* Mobile Card View */}
                      <div className="md:hidden space-y-3">
                        {paginatedPages.map((page) => (
                          <Card key={page.id} className="overflow-hidden">
                            <Collapsible
                              open={expandedPages.has(page.page_url)}
                              onOpenChange={() => togglePageExpand(page.page_url)}
                            >
                              <CardContent className="p-4">
                                {/* Row 1: URL */}
                                <CollapsibleTrigger className="w-full text-left">
                                  <p className="text-sm font-medium truncate text-primary">
                                    {page.page_url}
                                  </p>
                                </CollapsibleTrigger>
                                
                                {/* Row 2: Score + Stats */}
                                <div className="flex items-center justify-between mt-2">
                                  <div className="flex items-center gap-2">
                                    <span className={`text-2xl font-bold ${getScoreColor(page.performance_score)}`}>
                                      {page.performance_score}
                                    </span>
                                    <Badge variant={getScoreBadgeVariant(page.performance_score)} className="text-xs">
                                      {getScoreLabel(page.performance_score)}
                                    </Badge>
                                  </div>
                                  <Badge variant="outline" className="text-xs">
                                    {page.queries.length} queries
                                  </Badge>
                                </div>
                                
                                {/* Row 3: Metrics */}
                                <div className="grid grid-cols-4 gap-2 mt-3 text-xs text-muted-foreground">
                                  <div>
                                    <p className="font-medium text-foreground">{page.total_clicks.toLocaleString()}</p>
                                    <p>Clicks</p>
                                  </div>
                                  <div>
                                    <p className="font-medium text-foreground">{page.total_impressions.toLocaleString()}</p>
                                    <p>Impr</p>
                                  </div>
                                  <div>
                                    <p className="font-medium text-foreground">{(page.avg_ctr * 100).toFixed(1)}%</p>
                                    <p>CTR</p>
                                  </div>
                                  <div>
                                    <p className="font-medium text-foreground">{page.avg_position.toFixed(1)}</p>
                                    <p>Pos</p>
                                  </div>
                                </div>
                                
                                {/* Row 4: Button */}
                                <div className="flex justify-end mt-3">
                                  {page.task_id ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewTask(page.task_id!);
                                      }}
                                      className="text-green-600 border-green-600 hover:bg-green-50"
                                    >
                                      <CheckCircle className="h-4 w-4 mr-1" />
                                      Tasked
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddToTask(page);
                                      }}
                                      disabled={addingTask || isCreating}
                                    >
                                      <ListPlus className="h-4 w-4 mr-1" />
                                      Task
                                    </Button>
                                  )}
                                </div>
                                
                                {/* Expanded Queries */}
                                {page.queries.length > 0 && (
                                  <CollapsibleContent className="mt-3 pt-3 border-t">
                                    <p className="text-xs font-semibold mb-2">Top Queries:</p>
                                    <div className="space-y-2">
                                      {page.queries.slice(0, 5).map((query, idx) => (
                                        <div key={idx} className="text-xs border-l-2 border-primary pl-2 py-1">
                                          <p className="font-medium truncate">{query.query}</p>
                                          <div className="flex gap-2 text-muted-foreground mt-1">
                                            <span>Pos: {query.position.toFixed(1)}</span>
                                            <span>•</span>
                                            <span>{query.clicks} clicks</span>
                                          </div>
                                        </div>
                                      ))}
                                      {page.queries.length > 5 && (
                                        <p className="text-xs text-muted-foreground">
                                          +{page.queries.length - 5} more queries
                                        </p>
                                      )}
                                    </div>
                                  </CollapsibleContent>
                                )}
                              </CardContent>
                            </Collapsible>
                          </Card>
                        ))}
                      </div>

                      {/* Desktop Table View */}
                      <Card className="hidden md:block">
                        <CardContent className="p-0">
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-[350px]">
                                    <SortButton field="page_url">Page URL</SortButton>
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <SortButton field="performance_score">Score</SortButton>
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <SortButton field="total_clicks">Clicks</SortButton>
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <SortButton field="total_impressions">Impressions</SortButton>
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <SortButton field="avg_ctr">CTR</SortButton>
                                  </TableHead>
                                  <TableHead className="text-right">
                                    <SortButton field="avg_position">Position</SortButton>
                                  </TableHead>
                                  <TableHead className="text-right">Queries</TableHead>
                                  <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {paginatedPages.map((page) => (
                                  <Collapsible
                                    key={page.id}
                                    open={expandedPages.has(page.page_url)}
                                    onOpenChange={() => togglePageExpand(page.page_url)}
                                    asChild
                                  >
                                    <>
                                      <TableRow className="cursor-pointer hover:bg-muted/50">
                                        <CollapsibleTrigger asChild>
                                          <TableCell className="font-medium">
                                            <div className="flex items-center gap-2">
                                              <span className="truncate max-w-[300px]">{page.page_url}</span>
                                            </div>
                                          </TableCell>
                                        </CollapsibleTrigger>
                                        <TableCell className="text-right">
                                          <div className="flex flex-col items-end gap-1">
                                            <span className={`text-2xl font-bold ${getScoreColor(page.performance_score)}`}>
                                              {page.performance_score}
                                            </span>
                                            <Badge variant={getScoreBadgeVariant(page.performance_score)}>
                                              {getScoreLabel(page.performance_score)}
                                            </Badge>
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right">{page.total_clicks.toLocaleString()}</TableCell>
                                        <TableCell className="text-right">{page.total_impressions.toLocaleString()}</TableCell>
                                        <TableCell className="text-right">{(page.avg_ctr * 100).toFixed(2)}%</TableCell>
                                        <TableCell className="text-right">{page.avg_position.toFixed(1)}</TableCell>
                                        <TableCell className="text-right">
                                          <Badge variant="outline">{page.queries.length}</Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                          {page.task_id ? (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewTask(page.task_id!);
                                              }}
                                              className="text-green-600 border-green-600 hover:bg-green-50"
                                            >
                                              <CheckCircle className="h-4 w-4 mr-1" />
                                              Tasked
                                            </Button>
                                          ) : (
                                            <Button
                                              size="sm"
                                              variant="secondary"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddToTask(page);
                                              }}
                                              disabled={addingTask || isCreating}
                                            >
                                              <ListPlus className="h-4 w-4 mr-1" />
                                              Task
                                            </Button>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                      {page.queries.length > 0 && (
                                        <CollapsibleContent asChild>
                                          <TableRow>
                                            <TableCell colSpan={8} className="bg-muted/30 p-4">
                                              <div className="space-y-3">
                                                <div className="flex items-center justify-between mb-3">
                                                  <p className="text-sm font-semibold">
                                                    Ranking Queries ({page.queries.length}):
                                                  </p>
                                                  <div className="flex items-center gap-2">
                                                    <span className="text-xs text-muted-foreground">Performance Score</span>
                                                    <Progress value={page.performance_score} className="w-24" />
                                                    <span className={`text-xs font-medium ${getScoreColor(page.performance_score)}`}>
                                                      {page.performance_score}%
                                                    </span>
                                                  </div>
                                                </div>
                                                {page.queries.slice(0, 10).map((query, idx) => (
                                                  <div
                                                    key={idx}
                                                    className="flex items-center justify-between text-sm border-l-2 border-primary pl-3 py-1 hover:bg-muted/50 rounded-r"
                                                  >
                                                    <span className="font-medium truncate max-w-[300px]">{query.query}</span>
                                                    <div className="flex items-center gap-4 text-muted-foreground">
                                                      <span className="flex items-center gap-1">
                                                        <Target className="h-3 w-3" />
                                                        Pos: {query.position.toFixed(1)}
                                                      </span>
                                                      <span className="flex items-center gap-1">
                                                        <MousePointer className="h-3 w-3" />
                                                        {query.clicks}
                                                      </span>
                                                      <span className="flex items-center gap-1">
                                                        <Eye className="h-3 w-3" />
                                                        {query.impressions.toLocaleString()}
                                                      </span>
                                                      <span>CTR: {(query.ctr * 100).toFixed(2)}%</span>
                                                    </div>
                                                  </div>
                                                ))}
                                                {page.queries.length > 10 && (
                                                  <p className="text-xs text-muted-foreground text-center pt-2">
                                                    Showing top 10 of {page.queries.length} queries
                                                  </p>
                                                )}
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                        </CollapsibleContent>
                                      )}
                                    </>
                                  </Collapsible>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Pagination Controls */}
                      {pages.length > 0 && (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                            <p className="text-xs sm:text-sm text-muted-foreground">
                              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                              {Math.min(currentPage * itemsPerPage, sortedPages.length)} of {sortedPages.length}
                            </p>
                            <Select
                              value={itemsPerPage.toString()}
                              onValueChange={(value) => {
                                setItemsPerPage(Number(value));
                                setCurrentPage(1);
                              }}
                            >
                              <SelectTrigger className="w-[100px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="25">25 / page</SelectItem>
                                <SelectItem value="50">50 / page</SelectItem>
                                <SelectItem value="100">100 / page</SelectItem>
                                <SelectItem value="500">500 / page</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2 justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                            >
                              Prev
                            </Button>
                            <span className="text-xs sm:text-sm">
                              {currentPage} / {Math.ceil(sortedPages.length / itemsPerPage)}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setCurrentPage(Math.min(Math.ceil(sortedPages.length / itemsPerPage), currentPage + 1))
                              }
                              disabled={currentPage >= Math.ceil(sortedPages.length / itemsPerPage)}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
